import { fmtBrl, fmtInt } from '../shared/formatters'
import { summarizeRaw, formatSummary, summarizeField } from '../shared/periods'
import { asRevealed, buildHybridDisplayRow } from '../rowBuilders'
import { buildBrandBreakdown } from '../aggregators/brandBreakdown'
import { openToBuy } from '../planning/openToBuy'
import { buildCategoryPositions, sumPositions } from '../planning/stockProjection'
import { buildCoverageValues } from '../planning/coverage'

import { SYSTEM_COLORS } from '../../../../components/system.colors';

export const buildStockRows = ({
  monthKeys,
  summaryPeriods,
  plano,
  realizado,
  estoquePecas,
  brandData,
  planoAgg,
  realizadoAgg,
  hybridAgg,
  pecasAgg,
  reaisAgg,
  plannedSalesRaw,
  showInventoryValueAtRetail,
}) => {
  const otbRow = openToBuy?.buildOpenToBuyLookingForActionsNeeded({ monthKeys, planoAgg, realizadoAgg, pecasAgg, summaryPeriods });

  const currentTimestamp = new Date();
  const currentMonthAndYearKey = `${currentTimestamp.getFullYear()}-${String(currentTimestamp.getMonth() + 1).padStart(2, '0')}`;

  let runningPurchase = 0;
  const accumulatedPurchase = monthKeys.map((key, i) => {
    if (key >= currentMonthAndYearKey) runningPurchase += otbRow?.rawValues?.[i] ?? 0;
    return runningPurchase;
  });

  const negativeStockColor = (v) => v == null
    ? SYSTEM_COLORS.GRAY.GRAY_187
    : v < 0 ? SYSTEM_COLORS.RED.BRICK_RED : SYSTEM_COLORS.BLACK.JET;

  const categoryPositions = buildCategoryPositions({
    estoquePecas,
    plano,
    realizado,
    monthKeys,
    currentMonthKey: currentMonthAndYearKey,
  });

  const sumByPosition = (monthIndex, project) => sumPositions(categoryPositions, monthIndex, project);

  const initialStockRaw = pecasAgg.map((d, i) => {
    if (monthKeys[i] <= currentMonthAndYearKey) return d._initialInventoryUnits ?? null;
    return sumByPosition(i, position => position.initial);
  });
  const eiSemRaw = summarizeRaw(initialStockRaw, summaryPeriods);

  const pmvFinalRaw = buildHybridDisplayRow({ monthKeys, hybridAgg, field: 'salePrice' }).rawValues;
  const valueAtPmv = (units, i) => units != null && pmvFinalRaw[i] != null ? units * pmvFinalRaw[i] : null;

  const initialValueAtPmvRaw = initialStockRaw.map(valueAtPmv);
  const initialValueAtPmvSemRaw = summarizeRaw(initialValueAtPmvRaw, summaryPeriods);

  const estoqueRows = [
    {
      key: "estoque-inicial", label: "Estoque Inicial", bold: true,
      affectedByEdit: true,
      values: initialStockRaw.map(v => v != null ? fmtInt(v) : null),
      rawValues: initialStockRaw,
      semesterValuesSummary: formatSummary(eiSemRaw, fmtInt),
      semRawValues: eiSemRaw,
      colorFn: negativeStockColor,
    },
  ];

  if (showInventoryValueAtRetail) {
    estoqueRows.push(asRevealed({
      key: "estoque-inicial-rs", label: "Estoque Inicial (R$)", bold: true,
      values: initialValueAtPmvRaw.map(v => v != null ? fmtBrl(v) : null),
      rawValues: initialValueAtPmvRaw,
      semesterValuesSummary: formatSummary(initialValueAtPmvSemRaw, fmtBrl),
      semRawValues: initialValueAtPmvSemRaw,
      colorFn: negativeStockColor,
    }));
  }

  estoqueRows.push({
    key: "estoque-carteira", label: "Carteira",
    values: pecasAgg.map(d => d.inboundInventoryUnits),
    semesterValuesSummary: summarizeField(pecasAgg, '_inboundInventoryUnits', summaryPeriods, fmtInt),
    breakdowns: buildBrandBreakdown({ data: brandData || [], monthKeys, field: 'inboundInventoryUnits' }),
  });

  if (showInventoryValueAtRetail) {
    estoqueRows.push(asRevealed({
      key: "estoque-carteira-rs", label: "Carteira (R$)",
      values: reaisAgg.map(d => d.realPortfolioValue),
      semesterValuesSummary: summarizeField(reaisAgg, '_realPortfolioValue', summaryPeriods, fmtBrl),
    }));
  }

  if (showInventoryValueAtRetail) {
    estoqueRows.push(asRevealed({
      key: "estoque-carteira-custo-medio", label: "Custo Médio da Carteira",
      values: monthKeys.map((_, i) => {
        const units = pecasAgg[i]?._inboundInventoryUnits;
        const value = reaisAgg[i]?._realPortfolioValue;
        return (units != null && value != null && units !== 0) ? fmtBrl(value / units) : null;
      }),
    }));
  }

  estoqueRows.push({
    key: "estoque-cobertura", label: "Cobertura Real",
    values: pecasAgg.map(d => d.actualInventoryCoverage),
  });

  estoqueRows.push({
    key: "estoque-cobertura-alvo", label: "Cobertura Alvo",
    values: pecasAgg.map(d => d.plannedInventoryCoverage),
  });

  const finalStockRaw = pecasAgg.map((d, i) => {
    if (monthKeys[i] < currentMonthAndYearKey) return d._endingInventoryUnits ?? null;
    return sumByPosition(i, position => position.final);
  });

  const efSemRaw = summarizeRaw(finalStockRaw, summaryPeriods);

  estoqueRows.push({
    key: "estoque-final", label: "Estoque Final REAL", bold: true, semHighlight: true,
    affectedByEdit: true,
    values: finalStockRaw.map(v => v != null ? fmtInt(v) : null),
    rawValues: finalStockRaw,
    semesterValuesSummary: formatSummary(efSemRaw, fmtInt),
    semRawValues: efSemRaw,
    colorFn: negativeStockColor,
  });

  const idealValueAtPmvRaw = pecasAgg.map((d, i) => valueAtPmv(d._idealInventoryUnits, i));
  const idealValueAtPmvSemRaw = summarizeRaw(idealValueAtPmvRaw, summaryPeriods);

  if (showInventoryValueAtRetail) {
    estoqueRows.push(asRevealed({
      key: "estoque-ideal-rs",
      label: "Estoque Ideal (R$)",
      values: idealValueAtPmvRaw.map(v => v != null ? fmtBrl(v) : null),
      semesterValuesSummary: formatSummary(idealValueAtPmvSemRaw, fmtBrl),
    }));
  }

  const finalValueAtPmvRaw = finalStockRaw.map(valueAtPmv);
  const finalValueAtPmvSemRaw = summarizeRaw(finalValueAtPmvRaw, summaryPeriods);

  if (showInventoryValueAtRetail) {
    estoqueRows.push(asRevealed({
      key: "estoque-final-rs", label: "Estoque Final (R$)", bold: true,
      values: finalValueAtPmvRaw.map(v => v != null ? fmtBrl(v) : null),
      rawValues: finalValueAtPmvRaw,
      semesterValuesSummary: formatSummary(finalValueAtPmvSemRaw, fmtBrl),
      semRawValues: finalValueAtPmvSemRaw,
      colorFn: negativeStockColor,
    }));
  }

  // Estoque de Apresentação ainda não existe no pipeline (Go → BQ → MySQL).
  // Mostrando 0 por enquanto; troca pelo campo real quando o wiring estiver pronto.
  estoqueRows.push({
    key: "estoque-apresentacao", label: "Estoque de Apresentação",
    values: monthKeys.map(() => fmtInt(0)),
    rawValues: monthKeys.map(() => 0),
    semesterValuesSummary: summaryPeriods.map(() => fmtInt(0)),
    semRawValues: summaryPeriods.map(() => 0),
  });

  estoqueRows.push({
    key: "estoque-ideal",
    label: "Estoque Final IDEAL",
    bold: true,
    values: pecasAgg.map(d => d.idealInventoryUnits),
    semesterValuesSummary: summarizeField(pecasAgg, '_idealInventoryUnits', summaryPeriods, fmtInt),
  });

  const purchaseRaw = monthKeys.map((key, i) => {
    if (key < currentMonthAndYearKey) return otbRow?.rawValues?.[i] ?? null;
    return sumByPosition(i, position => Math.max(position.ideal - position.final, 0));
  });
  const purchaseSemRaw = summarizeRaw(purchaseRaw, summaryPeriods);

  const coverageRow = estoqueRows.find(row => row.key === "estoque-cobertura");
  coverageRow.values = buildCoverageValues({
    monthKeys,
    currentMonthKey: currentMonthAndYearKey,
    finalStockRaw,
    plannedSalesRaw,
    pastCoverageValues: pecasAgg.map(d => d?.actualInventoryCoverage),
  });

  estoqueRows.push({
    key: "estoque-open-to-buy-gross", label: "OPEN TO BUY (Compra)", bold: true, semHighlight: true, semBlack: true,
    affectedByEdit: true,
    ...otbRow,
    linkOnCurrentMonth: true,
    values: purchaseRaw.map(v => v != null ? fmtInt(v) : null),
    rawValues: purchaseRaw,
    semesterValuesSummary: formatSummary(purchaseSemRaw, fmtInt),
    semRawValues: purchaseSemRaw,
    colorFn: (v) => v == null ? SYSTEM_COLORS.GRAY.GRAY_187 : v > 0 ? SYSTEM_COLORS.GREEN.FOREST_GREEN : SYSTEM_COLORS.BLACK.JET,
    bgFn: (v) => v == null || v === 0 ? "transparent" : SYSTEM_COLORS.GREEN.FOREST_GREEN_8,
  });

  const excessRaw = monthKeys.map((key, i) => {
    if (key < currentMonthAndYearKey) {
      const ideal = pecasAgg[i]?._idealInventoryUnits;
      const final = finalStockRaw[i];

      if (final == null || ideal == null) return null;

      return Math.max(final - ideal, 0);
    }

    return sumByPosition(i, position => Math.max(position.final - position.ideal, 0));
  });

  estoqueRows.push({
    key: "estoque-excesso", label: "EXCESSO (Liquidar)", bold: true,
    affectedByEdit: true,
    values: excessRaw.map(v => v != null ? fmtInt(v) : null),
    rawValues: excessRaw,
    colorFn: (v) => v == null ? SYSTEM_COLORS.GRAY.GRAY_187 : v > 0 ? SYSTEM_COLORS.RED.BRICK_RED : SYSTEM_COLORS.BLACK.JET,
    bgFn: (v) => v == null ? "transparent" : v > 0 ? SYSTEM_COLORS.RED.BRICK_RED_8 : "transparent",
  });

  return estoqueRows;
};